/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';

export interface ServicesCellProps {
  columnName: string;
  features: string[];
  gridApi: AgGridType;
};

const serviceSections = {
  alignItems     : 'center',
  cursor         : 'pointer',
  width          : '100%',
  justifyContent : 'left'
};

const useStyles = makeStyles(() => ({
  serviceSection : {
    ...serviceSections,
    display : 'inline-flex'
  },
  featureSectionTitle : {
    ...serviceSections,
    display : 'flex'
  }
}));

/**
 * Defines the ServicesCell component. This component is used to
 * display the Enabled or Disabled Services value within a tooltip.
 *
 * ``` typescript
 *
 *  import ServicesCell from './ServicesCell';
 * 
 *  const disabledServices = formatEnabledDisabledServices(device, false);
 *  const enabledServices  = formatEnabledDisabledServices(device, true);
 *
 *  <IonGrid  columnComponents={{
 *    disabledServicesCell : <ServicesCell features={disabledServices} gridApi={item?.api} />
 *    enabledServicesCell  : <ServicesCell features={enabledServices} gridApi={item?.api} />
 *  }} />
 *
 * ```
 */
const ServicesCell = ({features, gridApi, columnName}: ServicesCellProps) => {
  const classes                       = useStyles();
  const [openGroups, setOpenGroups]   = useState(false);
  const [columnWidth, setColumnWidth] = useState(0);

  useEffect(() => {
    const calculateVisibleServices = () => {
      const columnWidths = gridApi?.getColumnState()?.find(column => column?.colId === columnName)?.width;
      if (columnWidths) {
        setColumnWidth(columnWidths);
      }
    };
    calculateVisibleServices();
    gridApi?.addEventListener('columnResized', calculateVisibleServices);
    return () => {
      gridApi?.removeEventListener('columnResized', calculateVisibleServices);
    };
  }, [gridApi]);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenGroups(false);
      } else {
        setOpenGroups(true);
      }
    };
  }, [openGroups]);

  const getServicesTotalWidth = useCallback((features) => {
    return features?.reduce?.((total, feature) => total + feature?.length, 0);
  }, [features]);

  const getDisplayedServices = useCallback((features) => {
    let currentWidth      = 0;
    const paddingWidth    = 12;
    const marginWidth     = 4;
    const titleWidth      = 7;
    const displayedServices = [];
    const remainingServices = [];

    const totalGroupsWidth = getServicesTotalWidth(features) * titleWidth;

    if (totalGroupsWidth <= columnWidth) {
      return {
        displayedServices : features,
        remainingServices : []
      };
    }

    features?.forEach?.(feature => {
      const featureTitleWidth = feature?.length * titleWidth + paddingWidth + marginWidth;

      if (currentWidth + featureTitleWidth > columnWidth && displayedServices?.length) {
        remainingServices.push(feature);
      } else {
        currentWidth += featureTitleWidth;
        displayedServices.push(feature);
      } if (displayedServices?.length === 0 && features?.length) {
        displayedServices.push(features[0]);
        remainingServices?.splice(0, 1);
      }
    });

    return {
      displayedServices,
      remainingServices
    };
  }, [columnWidth, features]);

  const {
    displayedServices,
    remainingServices
  } = getDisplayedServices(features) || {};
  
  if (Array.isArray(features) && features?.length > 0) {
    return (
      <>
        <ClickAwayListener onClickAway={toggleTooltip()}
          mountListener>
          <Tooltip arrow
            open={openGroups}
            onClose={toggleTooltip()}
            interactive={true}
            title={
              <>
                {features?.map?.((feature, index) => (
                  <div key={index}
                    className={classes.featureSectionTitle}>
                    <span>{feature}</span>
                  </div>
                ))}
              </>
            }
            placement='bottom'>
            <div className={classes.serviceSection}
              onMouseOver={toggleTooltip(true)}>
              {displayedServices?.map?.((feature, index) => {
                return (
                  <div key={index}>
                    {`${index !== 0 ? ',' : ''} ${feature}`}
                  </div>
                );
              })}
              {remainingServices?.length >= 1 && (
                <span>...</span>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
      </>
    );
  };
  return <div />;
};

export default ServicesCell;
